'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { Button } from '@/shared/ui/shadcn/button';
import { Alert, AlertDescription } from '@/shared/ui/shadcn/alert';
import {
  AlertCircle,
  Maximize2,
  RotateCw,
  Loader2,
  Check,
  X,
  SkipForward,
} from 'lucide-react';
import { createLogger } from '@/shared/lib/logger';
import type { ImageSizePolicy } from '@/shared/lib/services/image-upload.service';
import {
  useImageLoader,
  useCropCanvas,
  useCropInteraction,
  CropOverlay,
  type CropArea,
} from './image-crop';
import {
  ASPECT_RATIOS,
  calculateInitialCropRect,
  findClosestAspectRatio,
  invertedAspectRatio,
  type AspectRatioKey,
} from '@/shared/constants/aspect-ratios';

const logger = createLogger('UnifiedImageCropModal');

// Fullscreen dark panel — same look as the Apple/Android croppers. The
// edge-to-edge layout comes from DialogContent's `fullScreen` variant (which
// skips the responsive bottom-sheet / centered-modal positioning); here we only
// own the surface: black background, white text, vertical flex layout.
const FULLSCREEN_DARK_PANEL =
  'flex flex-col w-full h-full bg-black backdrop-blur-none text-white';

interface UnifiedImageCropModalProps {
  imageUrl: string;
  aspectRatio?: number;
  isOpen?: boolean;
  /**
   * Legacy path: keep the cropper open (saving spinner) until the JPEG is
   * compressed, then hand the final blob over. Used by surfaces whose own
   * optimistic UI opens only once processing is done (e.g. angle upload modal).
   */
  onSave?: (croppedBlob: Blob) => void | Promise<void>;
  /**
   * Instant path (opt-in): fire the moment the user confirms with a synchronous
   * preview + a background blob promise, so the caller can show an optimistic
   * placeholder and close the cropper immediately. When provided, this takes
   * precedence over `onSave`.
   */
  onInstantConfirm?: (
    previewUrl: string | null,
    blobPromise: Promise<Blob | null>
  ) => void;
  /** Instant-path counterpart of `onSkipCrop` (uses the original file as-is). */
  onInstantSkip?: () => void;
  onCancel: () => void;
  onSkipCrop?: () => void | Promise<void>;
  title?: string;
  saveButtonText?: string;
  minWidth?: number;
  minHeight?: number;
  showSkipButton?: boolean;
  /**
   * Free-aspect crop mode: the frame resizes freely and snaps to the nearest
   * supported ratio on release (initial ratio auto-detected). When false the
   * crop is locked to the fixed `aspectRatio`.
   */
  freeAspectRatio?: boolean;
  /** Initial aspect ratio key (auto-detected if not provided) */
  initialAspectRatio?: AspectRatioKey;
  /** Shared downscale + 16px-align policy (angle ~2MP area, reference 768px, control ~1MP). */
  sizePolicy: Pick<ImageSizePolicy, 'mode' | 'target'>;
  /** JPEG output size budget in bytes. Defaults to 900KB. */
  maxFileSize?: number;
}

interface CropToolButtonProps {
  icon: typeof Maximize2;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

/** Bottom-bar tool: icon with a small caption, like the Apple cropper. */
function CropToolButton({
  icon: Icon,
  label,
  onClick,
  disabled,
}: CropToolButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex flex-col items-center gap-1.5 text-white transition-opacity hover:opacity-80 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 rounded-md px-2 py-1"
    >
      <Icon className="w-5 h-5" />
      <span className="text-chip">{label}</span>
    </button>
  );
}

export function UnifiedImageCropModal({
  imageUrl,
  aspectRatio: propAspectRatio,
  isOpen = true,
  onSave,
  onInstantConfirm,
  onInstantSkip,
  onCancel,
  onSkipCrop,
  title,
  saveButtonText,
  minWidth = 100,
  minHeight = 100,
  showSkipButton = false,
  freeAspectRatio = false,
  initialAspectRatio,
  sizePolicy,
  maxFileSize,
}: UnifiedImageCropModalProps) {
  const t = useTranslations('imageCrop');
  const tCommon = useTranslations('common');
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Set while snapping a free resize to its nearest ratio: the crop area was
  // already adjusted in place, so the ratio-change effect below must skip its
  // recenter to avoid undoing the snap.
  const suppressRecenterRef = useRef(false);

  // User-driven 90° rotation produces a new blob URL we feed back into the
  // loader. Keeping a separate state lets us revoke the previous object URL.
  const [rotatedImageUrl, setRotatedImageUrl] = useState<string | null>(null);
  const effectiveImageUrl = rotatedImageUrl ?? imageUrl;

  // Image loading
  const { imageLoaded, imageError, imageNaturalSize } = useImageLoader({
    imageUrl: effectiveImageUrl,
  });

  // Revoke any locally created blob URL when it's replaced or on unmount.
  // The cleanup runs before the next effect (i.e. when rotatedImageUrl changes)
  // and at unmount, so all created URLs are released exactly once.
  useEffect(() => {
    return () => {
      if (rotatedImageUrl) URL.revokeObjectURL(rotatedImageUrl);
    };
  }, [rotatedImageUrl]);
  // Drop any stale rotation when the upstream source URL changes.
  useEffect(() => {
    setRotatedImageUrl(null);
  }, [imageUrl]);

  // Aspect ratio state (for selector mode). Null only until auto-detection
  // picks the closest supported ratio. Dragging a handle resizes freely, but
  // the frame snaps back to one of the fixed supported ratios on release — the
  // committed crop is always a supported ratio (same rule as Apple/Android).
  const [selectedAspectRatioKey, setSelectedAspectRatioKey] =
    useState<AspectRatioKey | null>(initialAspectRatio ?? null);

  // Compute actual aspect ratio value
  const aspectRatio = freeAspectRatio
    ? selectedAspectRatioKey
      ? ASPECT_RATIOS[selectedAspectRatioKey].ratio
      : undefined
    : propAspectRatio;

  // Crop area state
  const [cropArea, setCropArea] = useState<CropArea>({
    x: 0,
    y: 0,
    width: 100,
    height: 100,
  });

  // Laid-out (display) size of the visible <img>. The crop frame lives in the
  // image's natural-pixel space and CropOverlay scales it into display pixels
  // by this size. It MUST track the element's real layout — measuring once
  // inline during render (as before) captured a stale/zero rect when the render
  // ran before the <img> had decoded/laid out, which scaled the frame to
  // 0/NaN and parked it off-screen for the whole session. A ResizeObserver plus
  // an onLoad re-measure keeps it correct across late decode, viewport resize
  // and orientation changes.
  const [imageDisplaySize, setImageDisplaySize] = useState({
    width: 0,
    height: 0,
  });
  // The displayed <img> can fail independently of useImageLoader's copy (e.g.
  // its blob decodes late or is revoked); surface that as an error instead of a
  // silent black frame.
  const [displayImageError, setDisplayImageError] = useState(false);

  const measureImage = useCallback(() => {
    const img = imageRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    setImageDisplaySize((prev) =>
      prev.width === rect.width && prev.height === rect.height
        ? prev
        : { width: rect.width, height: rect.height }
    );
  }, []);

  // Keep the display size in sync with the actual laid-out <img>.
  useEffect(() => {
    if (!imageLoaded) return;
    const img = imageRef.current;
    if (!img) return;
    measureImage();
    const observer = new ResizeObserver(() => measureImage());
    observer.observe(img);
    return () => observer.disconnect();
  }, [imageLoaded, effectiveImageUrl, measureImage]);

  // Reset the per-source error when the source URL changes.
  useEffect(() => {
    setDisplayImageError(false);
  }, [effectiveImageUrl]);

  // Canvas/crop operations
  // Instant path (onInstantConfirm) closes the cropper immediately and
  // compresses in the background; the legacy path (onSave) keeps it open until
  // the blob is ready, showing the saving spinner.
  const { cropImage } = useCropCanvas({ sizePolicy, maxFileSize });
  const [isSaving, setIsSaving] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);

  // Reflect a free-resize snap in the selector without re-centering the frame
  // (the crop area was already snapped in place by the interaction hook).
  const handleRatioSnap = useCallback(
    (key: AspectRatioKey) => {
      if (key === selectedAspectRatioKey) return;
      suppressRecenterRef.current = true;
      setSelectedAspectRatioKey(key);
    },
    [selectedAspectRatioKey]
  );

  // Crop interaction (drag/resize). In selector mode the frame resizes freely
  // and snaps to the nearest supported ratio on release; otherwise it stays
  // locked to the fixed `aspectRatio`.
  const { handleMouseDown, handleTouchStart } = useCropInteraction({
    imageRef,
    imageNaturalSize,
    aspectRatio,
    minWidth,
    minHeight,
    onCropAreaChange: setCropArea,
    cropArea,
    snapToNearestRatio: freeAspectRatio,
    onRatioSnap: handleRatioSnap,
  });

  // Auto-detect aspect ratio on image load (only if selector is shown and no initial ratio)
  useEffect(() => {
    if (
      freeAspectRatio &&
      !initialAspectRatio &&
      imageLoaded &&
      imageNaturalSize.width &&
      imageNaturalSize.height
    ) {
      const detectedRatio = findClosestAspectRatio(
        imageNaturalSize.width,
        imageNaturalSize.height
      );
      setSelectedAspectRatioKey(detectedRatio);
    }
  }, [
    freeAspectRatio,
    initialAspectRatio,
    imageLoaded,
    imageNaturalSize.width,
    imageNaturalSize.height,
  ]);

  // Set initial crop area after image is loaded
  useEffect(() => {
    if (!imageLoaded || !imageNaturalSize.width || !imageNaturalSize.height) {
      return;
    }

    // A free-resize snap already positioned the frame for this ratio — don't
    // recenter over it.
    if (suppressRecenterRef.current) {
      suppressRecenterRef.current = false;
      return;
    }

    if (aspectRatio) {
      // Use helper function to calculate optimal crop rect
      const rect = calculateInitialCropRect(
        imageNaturalSize.width,
        imageNaturalSize.height,
        aspectRatio
      );
      setCropArea(rect);
    } else {
      // Transient state while ratio auto-detection is pending — cover the
      // full image until the detected ratio re-runs this effect.
      setCropArea({
        x: 0,
        y: 0,
        width: imageNaturalSize.width,
        height: imageNaturalSize.height,
      });
    }
  }, [
    imageLoaded,
    imageNaturalSize.width,
    imageNaturalSize.height,
    aspectRatio,
  ]);

  const handleSaveClick = useCallback(async () => {
    const img = imageRef.current;
    if (!img) return;

    if (onInstantConfirm) {
      // Capture the cropped pixels synchronously + an instant preview, then
      // hand off so the caller can show the optimistic card and close the
      // cropper right away. Compression continues on the detached canvas.
      const { previewUrl, blobPromise } = cropImage(img, cropArea, {
        emitPreview: true,
      });
      onInstantConfirm(previewUrl, blobPromise);
      return;
    }

    // Legacy path: keep the cropper visible (spinner) until the blob is ready.
    setIsSaving(true);
    try {
      const { blobPromise } = cropImage(img, cropArea);
      const blob = await blobPromise;
      if (blob) {
        await onSave?.(blob);
      }
    } finally {
      setIsSaving(false);
    }
  }, [cropImage, cropArea, onInstantConfirm, onSave]);

  const handleSkipCrop = useCallback(async () => {
    if (onInstantSkip) {
      onInstantSkip();
      return;
    }
    if (onSkipCrop) {
      setIsSkipping(true);
      try {
        await onSkipCrop();
      } finally {
        setIsSkipping(false);
      }
    }
  }, [onInstantSkip, onSkipCrop]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      logger.debug('Key pressed:', e.key);
      if (e.key === 'Enter') {
        logger.debug('Enter pressed - submitting crop');
        e.preventDefault();
        e.stopPropagation();
        handleSaveClick();
      }
    },
    [handleSaveClick]
  );

  // Maximize the crop area while keeping the active ratio — the crop must
  // never leave the fixed ratio set, so "fit" re-centers at max coverage
  // instead of expanding to the full (free-ratio) image.
  const handleFitToWindow = useCallback(() => {
    if (!imageNaturalSize.width || !imageNaturalSize.height) return;
    if (aspectRatio) {
      setCropArea(
        calculateInitialCropRect(
          imageNaturalSize.width,
          imageNaturalSize.height,
          aspectRatio
        )
      );
    } else {
      setCropArea({
        x: 0,
        y: 0,
        width: imageNaturalSize.width,
        height: imageNaturalSize.height,
      });
    }
  }, [imageNaturalSize, aspectRatio]);

  const handleRotate90CW = useCallback(() => {
    const img = imageRef.current;
    if (!img || !img.complete || !img.naturalWidth || !img.naturalHeight) {
      return;
    }
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const canvas = document.createElement('canvas');
    canvas.width = h;
    canvas.height = w;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.translate(h / 2, w / 2);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(img, -w / 2, -h / 2);
    canvas.toBlob((blob) => {
      if (!blob) {
        logger.warn('Rotation toBlob returned null (canvas likely tainted)');
        return;
      }
      const url = URL.createObjectURL(blob);
      // The [rotatedImageUrl] cleanup effect revokes the previous URL on swap.
      setRotatedImageUrl(url);
      if (selectedAspectRatioKey) {
        setSelectedAspectRatioKey(invertedAspectRatio(selectedAspectRatioKey));
      }
    }, 'image/jpeg', 0.92);
  }, [selectedAspectRatioKey]);

  const isBusy = isSaving || isSkipping;

  // Error state
  if (imageError || displayImageError) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">
              {tCommon('error')}
            </DialogTitle>
          </DialogHeader>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {imageError ?? t('loadError')}
            </AlertDescription>
          </Alert>
          <div className="flex justify-end">
            <Button onClick={onCancel}>{tCommon('close')}</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Loading state — same dark fullscreen panel so the open sequence doesn't
  // flash a light dialog before the cropper appears.
  if (!imageLoaded) {
    return (
      <Dialog open={isOpen} onOpenChange={() => {}}>
        <DialogContent
          className={FULLSCREEN_DARK_PANEL}
          fullScreen
          showCloseButton={false}
        >
          <DialogTitle className="sr-only">
            {tCommon('loadingImage')}
          </DialogTitle>
          <div className="flex-1 flex flex-col items-center justify-center gap-6">
            <div className="flex items-center text-white">
              <Loader2 className="h-8 w-8 animate-spin me-3" />
              <span>{tCommon('loading')}</span>
            </div>
            {showSkipButton && (
              <Button
                variant="ghost"
                onClick={handleSkipCrop}
                disabled={isBusy}
                className="text-white hover:bg-white/10 hover:text-white"
              >
                {t('skipButton')}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        logger.debug('Dialog onOpenChange fired, open:', open);
        if (!open) {
          logger.debug('Dialog close attempt detected');
          return;
        }
      }}
    >
      <DialogContent
        className={FULLSCREEN_DARK_PANEL}
        fullScreen
        showCloseButton={false}
        onKeyDown={handleKeyDown}
        onPointerDownOutside={(e) => {
          logger.debug('Pointer down outside dialog - preventing close');
          e.preventDefault();
        }}
        onInteractOutside={(e) => {
          logger.debug('Interact outside dialog - preventing close');
          e.preventDefault();
        }}
      >
        {/* Header: cancel (X) left, centered title, confirm (✓) right —
            mirrors the Apple/Android croppers. */}
        <div className="grid grid-cols-[2.5rem_1fr_2.5rem] items-center gap-2 px-4 pt-4 pb-3">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onCancel}
            disabled={isBusy}
            aria-label={tCommon('cancel')}
            className="text-white hover:bg-white/10 hover:text-white rounded-full"
          >
            <X className="w-5 h-5" />
          </Button>
          <DialogTitle className="text-center text-white truncate">
            {title || t('title')}
          </DialogTitle>
          <Button
            type="button"
            size="icon"
            onClick={() => handleSaveClick()}
            disabled={isBusy}
            aria-label={saveButtonText || t('saveButton')}
            className="rounded-full"
          >
            {isBusy ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Check className="w-5 h-5" />
            )}
          </Button>
        </div>

        <div className="flex-1 flex items-center justify-center min-h-0 overflow-hidden touch-none px-4 py-3">
          <div
            ref={containerRef}
            className="relative inline-flex items-center justify-center touch-none"
            style={{ touchAction: 'none' }}
          >
            <div className="relative">
              <img
                ref={imageRef}
                src={effectiveImageUrl}
                alt="Edit preview"
                className="block w-auto h-auto"
                style={{
                  maxWidth: '94vw',
                  maxHeight: 'calc(100dvh - 14rem)',
                }}
                draggable={false}
                onLoad={measureImage}
                onError={() => setDisplayImageError(true)}
              />

              {imageDisplaySize.width > 0 &&
                imageDisplaySize.height > 0 &&
                imageNaturalSize.width > 0 &&
                imageNaturalSize.height > 0 && (
                  <CropOverlay
                    cropArea={cropArea}
                    imageNaturalSize={imageNaturalSize}
                    imageRect={imageDisplaySize}
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                  />
                )}
            </div>
          </div>
        </div>

        {/* Bottom toolbar: icon tools only (Fit / Rotate / Skip) — cancel and
            confirm live in the header. */}
        <div className="flex items-start justify-center gap-10 px-4 pt-2 pb-6">
          <CropToolButton
            icon={Maximize2}
            label={t('fit')}
            onClick={handleFitToWindow}
            disabled={isBusy}
          />
          <CropToolButton
            icon={RotateCw}
            label={t('rotate')}
            onClick={handleRotate90CW}
            disabled={isBusy || !imageLoaded}
          />
          {showSkipButton && (onSkipCrop || onInstantSkip) && (
            <CropToolButton
              icon={SkipForward}
              label={t('skipButton')}
              onClick={() => void handleSkipCrop()}
              disabled={isBusy}
            />
          )}
        </div>

        {/* Usage hint mirrors the Android cropper — tells first-time users the
            crop area is draggable and corner-resizable (not obvious on touch). */}
        <p className="mx-auto max-w-[400px] px-4 pb-6 text-center text-chip text-white/70">
          {t('instructions')}
        </p>
      </DialogContent>
    </Dialog>
  );
}
